import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import {
  Zap, Clock, AlertCircle, CheckCircle, Play, RefreshCw,
  Phone, MessageSquare, ScrollText, ChevronDown, ChevronUp
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const fmtDate = (d) => { try { return d ? format(parseISO(d), 'MMM d, h:mm a') : 'Never'; } catch { return 'Never'; } };

const CATEGORY_COLORS = {
  'Intelligence': 'bg-blue-100 text-blue-700',
  'Relationships': 'bg-teal-100 text-teal-700',
  'Distribution': 'bg-amber-100 text-amber-700',
  'Content': 'bg-purple-100 text-purple-700',
  'Real Estate': 'bg-green-100 text-green-700',
  'Instagram': 'bg-pink-100 text-pink-700',
  'TikTok': 'bg-orange-100 text-orange-700',
  'LinkedIn': 'bg-indigo-100 text-indigo-700',
  'Custom CRM': 'bg-primary/10 text-primary',
};

const PHASE_LABELS = { 1: 'Phase 1', 2: 'Phase 2', 3: 'Phase 3', 4: 'Live' };
const SMS_BOTS = [4, 7];

export default function AutomationHub() {
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [runningId, setRunningId] = useState(null);
  const [testingSms, setTestingSms] = useState(false);
  const [botAlerts, setBotAlerts] = useState({});
  const [expandedAlerts, setExpandedAlerts] = useState({});

  useEffect(() => { loadBots(); }, []);

  const loadBots = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/automation/bots');
      setBots(res.data || []);
      // Load alerts for SMS bots
      for (const bn of SMS_BOTS) {
        try {
          const aRes = await api.get(`/api/automation/bots/${bn}/alerts?limit=3`);
          setBotAlerts(prev => ({ ...prev, [bn]: aRes.data || [] }));
        } catch {}
      }
    } catch { toast.error('Failed to load bots'); }
    finally { setLoading(false); }
  };

  const testSms = async () => {
    setTestingSms(true);
    try {
      const res = await api.post('/api/automation/test-sms');
      if (res.data.sent) {
        toast.success(`✅ Test SMS sent to ${res.data.to}!`);
      } else {
        toast.error('SMS failed — check Twilio credentials');
      }
    } catch { toast.error('SMS test failed'); }
    finally { setTestingSms(false); }
  };

  const toggleBot = async (botNumber, isActive) => {
    const bot = bots.find(b => b.bot_number === botNumber);
    if (bot?.requires_setup) {
      toast.warning(`This bot requires setup: ${bot.requires_setup}`);
      return;
    }
    setTogglingId(botNumber);
    try {
      await api.patch(`/api/automation/bots/${botNumber}`, { is_active: isActive });
      setBots(prev => prev.map(b => b.bot_number === botNumber ? { ...b, is_active: isActive } : b));
      toast.success(isActive ? 'Bot activated' : 'Bot deactivated');
    } catch { toast.error('Failed to toggle bot'); }
    finally { setTogglingId(null); }
  };

  const runBot = async (botNumber) => {
    setRunningId(botNumber);
    try {
      const res = await api.post(`/api/automation/bots/${botNumber}/run`);
      if (res.data.status === 'pending_setup') {
        toast.warning(res.data.message);
      } else {
        toast.success(res.data.message || 'Bot triggered');
        loadBots();
      }
    } catch { toast.error('Failed to run bot'); }
    finally { setRunningId(null); }
  };

  const activeBots = bots.filter(b => b.is_active).length;

  return (
    <div className="space-y-5">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title">Automation Hub</h1>
          <p className="page-subtitle">14 bots managing your Hamptons operation — {activeBots} active</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={testSms} disabled={testingSms} variant="outline" size="sm"
            className="flex items-center gap-1.5" data-testid="test-sms-btn">
            <Phone size={13} />
            {testingSms ? 'Sending...' : 'Test SMS'}
          </Button>
          <Button onClick={loadBots} variant="outline" size="sm">
            <RefreshCw size={14} className="mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Twilio status banner */}
      <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-teal-50 border border-teal-200">
        <Phone size={14} className="text-teal-600 flex-shrink-0" />
        <span className="text-xs font-semibold text-teal-700">RingCentral SMS Connected</span>
        <span className="text-xs text-muted-foreground">Alerts sent to +1 929-790-3455 · from +1 929-436-3502 (RingCentral)</span>
        <span className="ml-auto flex items-center gap-1.5 text-xs text-teal-600 font-medium">
          <span className="w-2 h-2 rounded-full bg-teal-500" /> Active
        </span>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4">
          <div className="text-2xl font-bold text-foreground">{activeBots}</div>
          <div className="text-xs text-muted-foreground mt-0.5">Active Bots</div>
          <Progress value={(activeBots / 14) * 100} className="h-1 mt-2" />
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-foreground">{bots.filter(b => b.requires_setup).length}</div>
          <div className="text-xs text-muted-foreground mt-0.5">Needs Setup</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-foreground">{14 - activeBots - bots.filter(b => b.requires_setup).length}</div>
          <div className="text-xs text-muted-foreground mt-0.5">Ready to Activate</div>
        </Card>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {bots.map(bot => {
            const isSmsBot = SMS_BOTS.includes(bot.bot_number);
            const alerts = botAlerts[bot.bot_number] || [];
            const showAlerts = expandedAlerts[bot.bot_number];
            const canRun = [1, 2, 4, 5, 7].includes(bot.bot_number);

            return (
              <div
                key={bot.id}
                className={`bot-card ${bot.is_active ? 'active' : bot.requires_setup ? 'pending' : ''}`}
                data-testid={`automation-bot-card-${bot.bot_number}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold text-muted-foreground">#{String(bot.bot_number).padStart(2, '0')}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[bot.category] || 'bg-muted text-muted-foreground'}`}>
                      {bot.category}
                    </span>
                    {isSmsBot && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-teal-100 text-teal-700 flex items-center gap-1">
                        <Phone size={9} /> SMS
                      </span>
                    )}
                  </div>
                  <Switch
                    checked={bot.is_active}
                    onCheckedChange={(checked) => toggleBot(bot.bot_number, checked)}
                    disabled={togglingId === bot.bot_number || bot.bot_number === 14}
                    data-testid={`automation-bot-toggle-${bot.bot_number}`}
                  />
                </div>

                <div className="mb-2">
                  <div className="font-semibold text-sm text-foreground">{bot.name}</div>
                  <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{bot.description}</div>
                </div>

                <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                  <div className="flex items-center gap-1"><Clock size={11} /><span>{bot.schedule}</span></div>
                  {bot.time_saved && <span className="text-amber-600 font-medium">{bot.time_saved}</span>}
                </div>

                {bot.requires_setup ? (
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-50 border border-amber-200">
                    <AlertCircle size={13} className="text-amber-600 flex-shrink-0" />
                    <span className="text-xs text-amber-700">Requires: {bot.requires_setup}</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-xs">
                        {bot.is_active
                          ? <><CheckCircle size={12} className="text-teal-600" /><span className="text-teal-700">Active</span></>
                          : <><div className="w-2 h-2 rounded-full bg-muted-foreground/30" /><span className="text-muted-foreground">Ready</span></>
                        }
                      </div>
                      <div className="flex items-center gap-1.5">
                        {bot.last_run && <span className="text-xs text-muted-foreground">{fmtDate(bot.last_run)}</span>}
                        {canRun && (
                          <Button size="sm" variant="outline" className="h-6 text-xs px-2"
                            onClick={() => runBot(bot.bot_number)} disabled={runningId === bot.bot_number}
                            data-testid={`bot-run-btn-${bot.bot_number}`}>
                            <Play size={9} className="mr-1" />
                            {runningId === bot.bot_number ? '...' : 'Run'}
                          </Button>
                        )}
                        {isSmsBot && alerts.length > 0 && (
                          <Button size="sm" variant="ghost" className="h-6 text-xs px-1.5"
                            onClick={() => setExpandedAlerts(p => ({ ...p, [bot.bot_number]: !showAlerts }))}>
                            <ScrollText size={10} className="mr-1" />
                            {alerts.length}
                            {showAlerts ? <ChevronUp size={9} className="ml-1" /> : <ChevronDown size={9} className="ml-1" />}
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Last result preview for SMS bots */}
                    {isSmsBot && bot.last_result && (
                      <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-2 leading-relaxed line-clamp-2">
                        {bot.last_result}
                      </div>
                    )}

                    {/* Alert history */}
                    {isSmsBot && showAlerts && alerts.length > 0 && (
                      <div className="space-y-1.5 pt-1 border-t border-border">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Recent Alerts</p>
                        {alerts.map((a, i) => (
                          <div key={i} className="flex items-start gap-2 text-xs p-2 rounded bg-muted/40">
                            <MessageSquare size={10} className="text-primary mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-muted-foreground truncate">{a.content?.slice(0, 100)}...</div>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-muted-foreground/60">{fmtDate(a.timestamp)}</span>
                                {a.sms_sent && <span className="text-teal-600 font-medium">📱 SMS sent</span>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
